import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/signup/PhoneInput";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Phone, Loader2 } from "lucide-react";
import { countries } from "@/data/countries";

interface ProfileCompletionModalProps {
  isOpen: boolean;
  userId: string;
  userEmail: string;
  onComplete: () => void;
}

export const ProfileCompletionModal = ({
  isOpen,
  userId,
  userEmail,
  onComplete,
}: ProfileCompletionModalProps) => {
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("FR");
  const [loading, setLoading] = useState(false);

  const getFullPhone = () => {
    const country = countries.find((c) => c.code === countryCode);
    const dialCode = country?.dialCode || "+33";
    const cleaned = phone.replace(/\s/g, "");
    const local = cleaned.startsWith("0") ? cleaned.slice(1) : cleaned;
    return `${dialCode}${local}`;
  };

  const handleSubmit = async () => {
    const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
    if (cleaned.length < 6) {
      toast.error("Numéro de téléphone invalide");
      return;
    }

    const fullPhone = getFullPhone();
    setLoading(true);

    try {
      // Save phone to profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ phone: fullPhone })
        .eq("id", userId);

      if (profileError) throw profileError;

      // Link old phone-based tickets to this email
      const { error: linkError } = await supabase.functions.invoke("link-phone-tickets", {
        body: { userId, phone: fullPhone, email: userEmail },
      });

      if (linkError) {
        console.error("Error linking tickets:", linkError);
        // Non-blocking — profile is saved, tickets linking is best-effort
      }

      toast.success("Profil complété ! Vos anciens coupons ont été liés.");
      onComplete();
    } catch (error: any) {
      console.error("Error completing profile:", error);
      toast.error("Erreur lors de la mise à jour du profil");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    // Mark profile as "skipped" by setting phone to empty string explicitly
    // This way we don't show the modal again
    try {
      await supabase
        .from("profiles")
        .update({ phone: "" })
        .eq("id", userId);
    } catch {
      // ignore
    }
    onComplete();
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
              <Phone className="w-7 h-7 text-primary" />
            </div>
            <br />
            Complète ton profil
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Ajoute ton numéro de téléphone pour retrouver tes coupons gagnés par SMS.
          </p>

          <div className="space-y-2">
            <Label>Numéro de téléphone</Label>
            <PhoneInput
              value={phone}
              onChange={setPhone}
              countryCode={countryCode}
              onCountryChange={setCountryCode}
            />
          </div>

          <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
            <p>📧 Connecté avec : <strong>{userEmail}</strong></p>
            <p className="mt-1">📱 Si tu as déjà gagné des coupons par SMS, entre le même numéro pour les retrouver ici.</p>
          </div>

          <Button
            onClick={handleSubmit}
            className="w-full h-12"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "Enregistrer"
            )}
          </Button>

          <button
            onClick={handleSkip}
            className="w-full text-center text-sm text-muted-foreground underline"
            disabled={loading}
          >
            Passer cette étape
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
